import { useState, useEffect } from "react";
import { X, Share, PlusSquare, MoreVertical } from "lucide-react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";

const DISMISSED_KEY = "skoltend_install_dismissed";

export function InstallBanner() {
  const { platform, canInstall, isInstalled, triggerInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(true);
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    const val = localStorage.getItem(DISMISSED_KEY);
    if (!val) setDismissed(false);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
    setShowIOSModal(false);
  };

  if (isInstalled || dismissed || !canInstall) return null;

  const handleClick = async () => {
    if (platform === "ios") {
      setShowIOSModal(true);
    } else {
      const accepted = await triggerInstall();
      if (accepted) dismiss();
    }
  };

  return (
    <>
      {/* Bottom banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1a4731] text-white px-4 py-3 flex items-center gap-3 shadow-lg">
        <img src="/Logo-shield-192x192.png" alt="Skoltend" className="h-10 w-10 rounded-xl shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm leading-tight">加入主畫面</p>
          <p className="text-green-300 text-xs leading-tight truncate">快速存取每日學校招標資訊</p>
        </div>
        <button
          onClick={handleClick}
          className="shrink-0 bg-green-400 hover:bg-green-300 text-green-900 font-bold text-sm px-4 py-2 rounded-full transition-colors"
        >
          安裝
        </button>
        <button
          onClick={dismiss}
          className="shrink-0 text-green-400 hover:text-white transition-colors p-1"
          aria-label="關閉"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* iOS instruction modal */}
      {showIOSModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 px-4 pb-4">
          <div className="w-full max-w-sm bg-white rounded-2xl overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <img src="/Logo-shield-192x192.png" alt="Skoltend" className="h-10 w-10 rounded-xl" />
                <div>
                  <p className="font-bold text-slate-800 text-base">加入主畫面</p>
                  <p className="text-slate-500 text-xs">只需兩個步驟</p>
                </div>
              </div>
              <button onClick={dismiss} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">1</div>
                <div>
                  <p className="text-slate-800 font-medium text-sm">點擊底部「分享」按鈕</p>
                  <p className="text-slate-500 text-xs mt-0.5">即 Safari 底部工具列的</p>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 bg-slate-100 rounded-lg px-2.5 py-1.5">
                    <Share className="h-4 w-4 text-blue-500" />
                    <span className="text-slate-700 text-xs font-medium">分享</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold shrink-0 mt-0.5">2</div>
                <div>
                  <p className="text-slate-800 font-medium text-sm">選擇「加入主畫面」</p>
                  <p className="text-slate-500 text-xs mt-0.5">在分享選單中向下捲動</p>
                  <div className="mt-1.5 inline-flex items-center gap-1.5 bg-slate-100 rounded-lg px-2.5 py-1.5">
                    <PlusSquare className="h-4 w-4 text-slate-600" />
                    <span className="text-slate-700 text-xs font-medium">加入主畫面</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-5 pb-5">
              <button
                onClick={dismiss}
                className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold py-3 rounded-xl text-sm transition-colors"
              >
                明白了
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
